import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import DabClient from '../clients/DabClient'
import HyperLiquidClient from '../clients/HyperLiquidClient'
import OrdinalsClient from '../clients/OrdinalsClient'
import useConfig from '../hooks/useConfig'
import useWallet from '../hooks/useWallet'
import { type NFTCollection, type PlugToken, Chain } from '../interfaces/common'
import { logger } from '../logger'
import {
  useDevMode,
  useLocales,
  useNftBalances,
  useSelectedChain,
  useTokenBalances,
  useUserEVMChains,
  useUserTokens,
} from '../state'
import { PerpsAccountBalance } from '../state/tokenBalances'
import { getTokenBalance, getTokenBalances } from '../utils/getTokenBalances'
import { groupBy } from '../utils/miscHelper'
import { updateCkBTCBalance } from '../utils/updateCkBtcBalance'
import { e8sToDecimals } from '../utils/validationHelper'

export interface TokenBalance {
  token: PlugToken
  balance: bigint
  humanBalance: number
  usdBalance?: number
  tokenPrice?: number
  priceChange?: number
  fee?: bigint
}

interface TokenBalanceContextType {
  balances: TokenBalance[]
  walletNfts: NFTCollection[]
  nftsLoading: boolean
  refreshBalance: (token: PlugToken) => void
  findBalance: (query?: { id?: string; address?: string }) => TokenBalance | undefined
  tokensToBalances: (tokens: PlugToken[]) => TokenBalance[]
  loadNfts: () => Promise<void>
  filteredBalances: TokenBalance[]
  groupedBalances: GroupedBalance[]
  isEmptyWallet: boolean
  perpsAccountBalance?: PerpsAccountBalance
  refreshPerpsAccountBalance: () => Promise<void>
}

export const TokenBalanceContext = createContext<TokenBalanceContextType>(null as any)

export interface GroupedBalance {
  id: string
  name: string
  symbol: string
  totalHumanBalance: number
  logo: string
  balances: TokenBalance[]
  usdBalance?: number
  priceChange?: number
}

export const tokenBalanceToBalanceGroup = (tokenBalance: TokenBalance): GroupedBalance => {
  const { token, priceChange, usdBalance, balance, humanBalance } = tokenBalance

  return {
    id: token.id,
    name: token.name,
    symbol: token.symbol,
    totalHumanBalance: humanBalance,
    logo: token.logo,
    balances: [tokenBalance],
    usdBalance,
    priceChange,
  }
}

const BalanceProvider = ({ children }: PropsWithChildren) => {
  const [nftsLoading, setNftsLoading] = useState(false)

  const { tokens: allTokens, tokenPricesWithChange, tokenGroups } = useConfig()
  const { savedTokens } = useUserTokens()
  const { userEVMChains } = useUserEVMChains()

  const { wallet } = useWallet()

  const {
    balances: cachedBalances,
    setBalances,
    setBalance,
    setPerpsAccountBalance,
    perpsAccountBalances,
  } = useTokenBalances()
  const { nftBalances, setNftBalances } = useNftBalances()
  const { activeNetwork, networkTokens, devEnabled } = useDevMode()

  const { selectedChain } = useSelectedChain()
  const { userLocale } = useLocales()

  const tokens = useMemo(() => {
    if (devEnabled) return (networkTokens[activeNetwork || ''] || []).map((token) => token.id)

    return savedTokens[wallet.principalId || ''] || []
  }, [savedTokens, wallet, devEnabled])

  const loadAllTokensBalances = useCallback(async () => {
    const relevantTokens = allTokens.filter((t) => tokens.includes(t.id))
    const balances = await getTokenBalances(wallet, relevantTokens, userEVMChains)

    setBalances(wallet.principalId, balances)
  }, [wallet.accountId, wallet.principalId, allTokens])

  const refreshPerpsAccountBalance = useCallback(async () => {
    if (!wallet.ethAddress) return

    const hyperliquidClient = new HyperLiquidClient()

    try {
      const {
        marginSummary: { accountValue },
        withdrawable,
        assetPositions,
      } = await hyperliquidClient.getPerpAccountBalance(wallet.ethAddress)

      setPerpsAccountBalance(wallet.principalId, {
        totalBalance: parseFloat(accountValue),
        withdrawableBalance: parseFloat(withdrawable),
        assetPositions,
      })
    } catch (e) {
      logger.error('Error loading hyperliquid balances', { originalError: e })
    }
  }, [wallet.ethAddress])

  useEffect(() => {
    loadAllTokensBalances().catch(logger.debug)
  }, [loadAllTokensBalances])

  useEffect(() => {
    refreshPerpsAccountBalance()
  }, [refreshPerpsAccountBalance])

  const refreshBalance = useCallback(
    async (token: PlugToken) => {
      try {
        const evm: number[] = token.evmChain?.chainId != null ? [token.evmChain.chainId] : []
        const balance = await getTokenBalance(wallet, token, evm)

        setBalance(token.id, wallet.principalId, balance)
      } catch (e) {
        logger.debug('Failed to refresh balance', { originalError: e })
      }
    },
    [wallet, setBalance]
  )

  const balances: TokenBalance[] = useMemo(() => {
    const currentWalletBalances = cachedBalances[wallet.principalId] || []
    const tokenIds = new Set([...tokens, ...currentWalletBalances.map((b) => b.id)])

    return Array.from(tokenIds)
      .map((id) => {
        const token = allTokens.find((dt) => dt.id === id)!
        const balance = currentWalletBalances.find((tb) => tb.id === id)

        const tokenPrice = tokenPricesWithChange[id]
        const decimalBalance = e8sToDecimals(balance?.balance || 0, token?.decimals || 8)
        return {
          token: token,
          balance: BigInt(balance?.balance || 0),
          humanBalance: decimalBalance,
          tokenPrice: tokenPrice?.price,
          priceChange: tokenPrice?.priceChange,
          usdBalance: tokenPrice?.price ? decimalBalance * tokenPrice.price : undefined,
        }
      })
      .filter((tb) => !!tb.token)
      .sort((left, right) => (right.usdBalance || 0) - (left.usdBalance || 0))
  }, [allTokens, tokens, cachedBalances, wallet.principalId, tokenPricesWithChange])

  const findBalance: TokenBalanceContextType['findBalance'] = useCallback(
    (query) => {
      if (!query) return

      return balances.find((b) => {
        if (query.id) {
          return b.token.id === query.id
        } else if (query.address && b.token.address) {
          return b.token.address.toLowerCase() === query.address.toLowerCase()
        }
      })
    },
    [balances]
  )

  useEffect(() => {
    if (!wallet.principalId) return

    updateCkBTCBalance(wallet.principalId).catch((e) => {
      logger.error('Failed to update ckBTC balance', { originalError: e })
    })
  }, [wallet.principalId])

  const balancesMap = useMemo(
    () => Object.fromEntries(balances.map((b) => [b.token.id, b])),
    [balances]
  )

  const tokensToBalances: TokenBalanceContextType['tokensToBalances'] = useCallback(
    (tokens) =>
      tokens
        .map((t) => balancesMap[t.id] || { token: t, balance: 0, humanBalance: 0, usdBalance: 0 })
        .sort((a, b) => (b.usdBalance || 0) - (a.usdBalance || 0)),
    [balancesMap]
  )

  const loadNfts = useCallback(async () => {
    setNftsLoading(true)

    try {
      const client = new DabClient()
      const ordinalClient = new OrdinalsClient()

      const [nftsResult, ordinalsResult] = await Promise.allSettled([
        client.getUserNfts(wallet.principalId),
        ordinalClient.getOrdinals(wallet.btcAddress || ''),
      ])

      const nfts = nftsResult.status === 'fulfilled' ? nftsResult.value : []
      const ordinals = ordinalsResult.status === 'fulfilled' ? ordinalsResult.value : []

      if (nftsResult.status === 'rejected') {
        logger.error('Error loading DAB NFTs', { originalError: nftsResult.reason })
      }
      if (ordinalsResult.status === 'rejected') {
        logger.error('Error loading Ordinals', { originalError: ordinalsResult.reason })
      }

      setNftBalances(wallet.principalId, [
        ...nfts,
        ...ordinals.map((o) => ({
          canisterId: `ordinal-${o.number}`,
          name: o.name,
          nfts: [o],
          icon: o.url,
          chain: Chain.BTC,
        })),
      ])
    } catch (e) {
      logger.error('Error loading NFTs', { originalError: e })
    } finally {
      setNftsLoading(false)
    }
  }, [wallet.principalId])

  useEffect(() => {
    if (devEnabled) return

    loadNfts().catch(logger.error)
  }, [wallet.principalId, wallet.btcAddress, setNftBalances, devEnabled])

  const relevantTokenBalances = balances.filter((b) =>
    savedTokens[wallet.principalId]?.includes(b.token.id)
  )

  const filteredBalances = useMemo(() => {
    if (selectedChain === 'all') {
      return relevantTokenBalances.filter(
        (c) => !c.token.evmChain || userEVMChains.includes(c.token.evmChain.chainId)
      )
    }
    return relevantTokenBalances.filter((b) => b.token.chain === selectedChain)
  }, [selectedChain, relevantTokenBalances, userLocale])

  const groupedBalances: GroupedBalance[] = useMemo(() => {
    const ungroupedBalances = filteredBalances.filter((t) => !t.token.groupId)
    const groupedBalances = filteredBalances.filter((t) => t.token.groupId)

    const balancesByGroupId = groupBy(groupedBalances, (balance) => balance.token.groupId!)
    const ungroupedBalanceGroups = ungroupedBalances.map(tokenBalanceToBalanceGroup)

    const groupedBalanceGroups = Object.entries(balancesByGroupId).map(([groupId, tokens]) => {
      const tokenGroup = tokenGroups?.find((group) => group.id === groupId)

      if (tokens.length === 1) {
        return tokenBalanceToBalanceGroup(tokens[0])
      } else {
        const totalHumanBalance = tokens.reduce(
          (sum, token) => sum + e8sToDecimals(token.balance, token.token.decimals),
          0
        )
        const totalUsdBalance = tokens.reduce((sum, token) => sum + (token.usdBalance || 0), 0)

        return {
          id: groupId,
          name: tokenGroup?.name || tokens[0].token.name,
          symbol: tokenGroup?.symbol || tokens[0].token.symbol,
          totalHumanBalance,
          logo: tokenGroup?.logoUrl || tokens[0].token.logo,
          balances: tokens,
          usdBalance: totalUsdBalance,
          priceChange: tokens[0].priceChange,
        }
      }
    })

    return [...ungroupedBalanceGroups, ...groupedBalanceGroups].sort(
      (a, b) => (b.usdBalance || 0) - (a.usdBalance || 0)
    )
  }, [filteredBalances, tokenGroups])

  const isEmptyWallet = useMemo(() => {
    return balances.reduce((acc, next) => acc + next.balance, 0n) == 0n
  }, [balances])

  const perpsAccountBalance = useMemo(() => {
    return perpsAccountBalances[wallet.principalId]
  }, [wallet.principalId, perpsAccountBalances])

  const value = useMemo(() => {
    return {
      balances,
      walletNfts: nftBalances[wallet.principalId] || [],
      nftsLoading,
      refreshBalance,
      findBalance,
      tokensToBalances,
      loadNfts,
      filteredBalances,
      groupedBalances,
      isEmptyWallet,
      perpsAccountBalance,
      refreshPerpsAccountBalance,
    }
  }, [
    balances,
    nftsLoading,
    nftBalances,
    refreshBalance,
    findBalance,
    tokensToBalances,
    loadNfts,
    filteredBalances,
    groupedBalances,
    isEmptyWallet,
    perpsAccountBalance,
    refreshPerpsAccountBalance,
  ])

  return <TokenBalanceContext.Provider value={value}>{children}</TokenBalanceContext.Provider>
}

export default BalanceProvider
